import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import { RewardBadge } from './RewardBadge';
import { WorldMapLinks } from '../../world/WorldMapLinks';
import { EntityThumb } from '../../world/EntityThumb';
import { getEntry, getCategory, entryPathById, entryKindLabel } from '../../../utils/guideIndex';
import type { Block, EntryLink } from '../../../types/guide';
function LinkCard({
  link
}: {
  link: EntryLink;
}) {
  const target = getEntry(link.entryId);
  if (!target) return null;
  const cat = getCategory(target.categoryId);
  const Icon = cat?.icon;
  return <Link to={entryPathById(link.entryId, link.sectionId)} className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:bg-white/[0.04]" data-id="element-543" data-mp="38px8t2hte9u4">
      <EntityThumb entity={target.entity} name={target.title} fallback={<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]" data-id="element-545" data-mp="2b86ync38gz6p">
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" data-id="element-546" data-mp="302540y5njcjv" />}
          </span>} data-id="element-544" data-mp="2g79se0drjizd" />
      <span className="min-w-0 flex-1" data-id="element-547" data-mp="34uy5nd5tq4rq">
        <span className="flex flex-wrap items-center gap-2" data-id="element-548" data-mp="34vzt6h410jqc">
          <span className="font-cinzel font-bold text-white" data-id="element-549" data-mp="39ryd9sibnfky">{target.title}</span>
          <RewardBadge chance={link.chance} guaranteed={link.guaranteed} data-id="element-550" data-mp="2d38xc7q98x1m" />
        </span>
        <span className="mt-0.5 block text-xs text-gray-500" data-id="element-551" data-mp="2h42l913p6bui">{entryKindLabel(target)}{link.who ? ` · ${link.who}` : ''}</span>
        {link.note && <span className="mt-1.5 block text-sm text-gray-400" data-id="element-552" data-mp="2m2ng0l06k3bk">{link.note}</span>}
      </span>
      <ArrowRight className="mt-2 h-4 w-4 shrink-0 text-gray-500 transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-553" data-mp="2r3b9ddbvsw8y" />
    </Link>;
}
function BlockView({
  block
}: {
  block: Block;
}) {
  switch (block.type) {
    case 'text':
      return <div className="space-y-3" data-id="element-554" data-mp="3cryj9jz29s82">
          {block.paragraphs.map(p => <p key={p} data-id="element-555" data-mp="37v83yyv2z0np">{p}</p>)}
        </div>;
    case 'facts':
      return <dl className="divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-556" data-mp="3mrfltsj0npzt">
          {block.items.map(f => <div key={f.label} className="grid gap-1 px-4 py-3 sm:grid-cols-[160px_1fr] sm:gap-4" data-id="element-557" data-mp="3htnvv41xmdy9">
              <dt className="text-sm text-gray-500" data-id="element-558" data-mp="2u43c4ixyfcnx">{f.label}</dt>
              <dd className="text-white" data-id="element-559" data-mp="2p3yq6dea1pqb">{f.value}</dd>
            </div>)}
        </dl>;
    case 'list':
      return <ul className="space-y-2" data-id="element-560" data-mp="33twk79yw19nf">
          {block.items.map(item => <li key={item} className="flex gap-3" data-id="element-561" data-mp="2z1l8zayp61fk">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" aria-hidden="true" data-id="element-562" data-mp="2z0tqn1u42078" />
              {item}
            </li>)}
        </ul>;
    case 'steps':
      return <ol className="space-y-3" data-id="element-563" data-mp="26bd0ybxvkxep">
          {block.items.map((item, i) => <li key={item} className="flex items-start gap-3" data-id="element-564" data-mp="ltu04sgyv8lc" data-mpi={i}>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/50 font-cinzel text-sm font-bold text-[#D4AF37]" data-id="element-565" data-mp="qmetxtzvhlov">{i + 1}</span>
              <span className="pt-0.5 text-white" data-id="element-566" data-mp="gw2bc7bmc2ld">{item}</span>
            </li>)}
        </ol>;
    case 'callout':
      return <div className="flex gap-3 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] p-4" data-id="element-567" data-mp="14mfvlbnyox5e">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-568" data-mp="19khenwle0wgv" />
          <p className="text-gray-200" data-id="element-569" data-mp="vkgbgahnhcy8">{block.text}</p>
        </div>;
    case 'rewards':
      return <div className="space-y-4" data-id="element-570" data-mp="zl8ntcltjxyq">
          {block.groups.map(g => <div key={g.who} className="rounded-2xl border border-white/10 p-4 md:p-5" data-id="element-571" data-mp="3gae36feh8424">
              <p className="font-cinzel font-bold text-white" data-id="element-572" data-mp="3kdar8lmscsdg">{g.who}</p>
              {g.note && <p className="mt-1 text-sm text-gray-400" data-id="element-573" data-mp="4ej6pbe3qjh1">{g.note}</p>}
              <ul className="mt-3 divide-y divide-white/5" data-id="element-574" data-mp="4gvw29m6vw44">
                {g.lines.map(l => <li key={l.label} className="flex flex-col gap-2 py-3 first:pt-1 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-id="element-575" data-mp="3vrdvbjxaab69">
                    <span className="flex items-center gap-3 text-gray-200" data-id="element-576" data-mp="3lwc3uhfjcx0i">
                      {l.entryId && <EntityThumb entity={getEntry(l.entryId)?.entity} name={l.label} data-id="element-577" data-mp="ndx4k4eycect" />}
                      <span data-id="element-578" data-mp="ie2e21z39qsa">
                      {l.entryId ? <Link to={entryPathById(l.entryId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 transition-colors duration-150 hover:text-[#D4AF37]" data-id="element-579" data-mp="icr9w11qeh0n">
                          {l.label}
                        </Link> : l.label}
                      {l.detail && <span className="block text-sm text-gray-400" data-id="element-580" data-mp="2u2jrvp5w5xn7">{l.detail}</span>}
                      </span>
                    </span>
                    <RewardBadge chance={l.chance} guaranteed={l.guaranteed} data-id="element-581" data-mp="1dnnrguzpz22n" />
                  </li>)}
              </ul>
            </div>)}
        </div>;
    case 'worldMaps':
      return <WorldMapLinks names={block.names} data-id="element-582" data-mp="1nd7d28etha17" />;
    case 'links':
      return <div className="grid gap-3 sm:grid-cols-2" data-id="element-583" data-mp="1nfbkh0bxcr68">
          {block.items.map(l => <LinkCard key={`${l.entryId}-${l.who ?? ''}-${l.sectionId ?? ''}`} link={l} data-id="element-584" data-mp="1scaihtypli9p" />)}
        </div>;
  }
}
export function EntryBlocks({
  blocks
}: {
  blocks: Block[];
}) {
  return <div className="space-y-4" data-id="element-585" data-mp="21drrythm2lpf">
      {blocks.map((b, i) => <BlockView key={i} block={b} data-id="element-586" data-mp="26cuzckl2d40k" data-mpi={i} />)}
    </div>;
}
export { LinkCard };